import { motion } from 'framer-motion';
import { ArrowDown, Sparkles, Shield, Clock } from 'lucide-react';
import heroImage from '../assets/axis-vacuum-cleaning.jpg';

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Clean modern home interior"
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
      </div>

      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-10 w-72 h-72 bg-axis-400/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-axis-300/10 rounded-full blur-3xl animate-float-delay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto section-padding pt-32 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/20 rounded-full px-5 py-2 mb-8"
          >
            <Sparkles className="w-4 h-4 text-sand-300" />
            <span className="text-sm font-medium text-white/90">
              Serving Montgomery County & Surrounding Areas
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.05] mb-6 text-shadow"
          >
            Professional Cleaning
            <br />
            <span className="text-axis-300">You Can Trust</span>
            <br />
            <span className="italic font-medium text-sand-300">For Home & Office</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg sm:text-xl text-white/80 max-w-xl leading-relaxed mb-10 font-light"
          >
            We deliver reliable, thorough cleaning solutions for your home and office. Let us handle the dirt so you can enjoy a spotless, welcoming space every time.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <a
              href="#contact"
              className="inline-flex items-center justify-center bg-white text-axis-700 px-8 py-4 rounded-full text-base font-semibold hover:bg-axis-50 hover:shadow-xl hover:shadow-axis-500/20 hover:scale-105 transition-all duration-300"
            >
              Schedule Cleaning
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center border-2 border-white/30 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              View Our Services
            </a>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex flex-wrap gap-8"
          >
            {[
              { icon: Shield, label: 'Bonded & Insured' },
              { icon: Clock, label: 'Flexible Scheduling' },
              { icon: Sparkles, label: 'Detail-Oriented' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                  <item.icon className="w-5 h-5 text-axis-300" />
                </div>
                <span className="text-sm font-medium text-white/80">{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs text-white/50 uppercase tracking-widest">Scroll</span>
          <ArrowDown className="w-5 h-5 text-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
}
